import { useState, useCallback, forwardRef, useImperativeHandle, memo } from 'react';
import PropTypes from 'prop-types';
import PhoneCallNotification from './PhoneCallNotification';

const NotificationManager = memo(forwardRef(({ children }, ref) => {
  const [notificationQueue, setNotificationQueue] = useState([]);

  const showPhoneCallNotification = useCallback(({ customer, callData }) => {
    if (!customer && !callData) {
      return;
    }

    setNotificationQueue((prevQueue) => [
      ...prevQueue,
      {
        id: Date.now(),
        customer,
        callData,
      },
    ]);
  }, []);

  const hidePhoneCallNotification = useCallback(() => {
    setNotificationQueue((prevQueue) => prevQueue.slice(1));
  }, []);

  const handleNotification = useCallback(
    (payload) => {
      let message = payload?.message;

      if (typeof message === 'string') {
        try {
          message = JSON.parse(message);
        } catch (error) {
          return;
        }
      }

      const messageType = message?.type;

      if (messageType === 'income_phone_call_notification') {
        const callData = message?.data || {};

        if (!callData || typeof callData !== 'object') {
          return;
        }

        const direction = (callData.direction || '').toLowerCase();
        const isInbound = direction === 'inbound';

        const primaryName =
          (isInbound ? callData.from_name : callData.to_name) ||
          callData.from_name ||
          callData.to_name ||
          '';

        const primaryCustomerId =
          (isInbound ? callData.from_customer_id : callData.to_customer_id) ??
          callData.from_customer_id ??
          callData.to_customer_id ??
          null;

        const primaryCustomerType =
          (isInbound ? callData.from_customer_type : callData.to_customer_type) ??
          callData.from_customer_type ??
          callData.to_customer_type ??
          null;

        const primaryPhone =
          (isInbound ? callData.from_phone_number : callData.to_phone_number) ||
          callData.from_phone_number ||
          callData.to_phone_number ||
          null;

        const customerData = {
          name: primaryName,
          customer_id: primaryCustomerId,
          customer_type: primaryCustomerType,
          email: null,
          phone: primaryPhone,
          cell_phone: null,
        };

        if (!customerData.name && !customerData.phone) {
          return;
        }

        showPhoneCallNotification({
          customer: customerData,
          callData,
        });
      }
    },
    [showPhoneCallNotification],
  );

  useImperativeHandle(ref, () => ({
    handleNotification,
  }));

  const currentNotification = notificationQueue[0] || null;
  const remainingCount = notificationQueue.length > 1 ? notificationQueue.length - 1 : 0;

  return (
    <>
      {typeof children === 'function' ? children() : children}
      <PhoneCallNotification
        open={!!currentNotification}
        customer={currentNotification?.customer}
        callId={currentNotification?.callData?.id}
        callData={currentNotification?.callData}
        onClose={hidePhoneCallNotification}
        remainingCount={remainingCount}
      />
    </>
  );
}));

NotificationManager.propTypes = {
  children: PropTypes.oneOfType([PropTypes.node, PropTypes.func]),
};

NotificationManager.displayName = 'NotificationManager';

export default NotificationManager;
